/**
 * React Query hooks pour les appels (Calls API)
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { callsApi, CallLog, InitiateCallDto, UpdateCallDto, FilterCallsDto, TwilioTokenResponse } from '@/services/api/calls.api';

export function useCalls(
  filters?: FilterCallsDto,
  options?: Omit<UseQueryOptions<CallLog[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<CallLog[], Error>({
    queryKey: ['calls', filters],
    queryFn: () => callsApi.list(filters),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useTwilioToken(
  options?: Omit<UseQueryOptions<TwilioTokenResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TwilioTokenResponse, Error>({
    queryKey: ['twilio-token'],
    queryFn: () => callsApi.getToken(),
    staleTime: 30 * 60 * 1000,
    ...options,
  });
}

export function useInitiateCall(
  options?: Omit<UseMutationOptions<CallLog, Error, InitiateCallDto>, 'mutationFn'>
) {
  const queryClient = useQueryClient();
  return useMutation<CallLog, Error, InitiateCallDto>({
    mutationFn: (data) => callsApi.initiate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calls'] });
    },
    ...options,
  });
}

export function useUpdateCall(
  options?: Omit<UseMutationOptions<CallLog, Error, { id: string; data: UpdateCallDto }>, 'mutationFn'>
) {
  const queryClient = useQueryClient();
  return useMutation<CallLog, Error, { id: string; data: UpdateCallDto }>({
    mutationFn: ({ id, data }) => callsApi.update(id, data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['calls'] });
      queryClient.setQueryData(['calls', updated.id], updated);
    },
    ...options,
  });
}
